import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { organizationAPI, peopleAPI, getImageUrl } from '../services/api';
import ImageCropModal from '../components/ImageCropModal';
import Pagination from '../components/Pagination';
import { formatDate } from '../utils/dateTime';
import './People.css';

interface Organization {
  _id: string;
  name: string;
}

interface Person {
  _id: string;
  key: string;
  organization_id: string;
  image_url?: string;
  images?: Array<{ url: string; uploaded_at: string }>;
  created_at: string;
}

function People() {
  const { organizationId } = useParams<{ organizationId: string }>();
  const navigate = useNavigate();
  
  const [people, setPeople] = useState<Person[]>([]);
  const [organization, setOrganization] = useState<Organization | null>(null);
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({ key: '' });
  const [selectedFiles, setSelectedFiles] = useState<File[]>([]);
  const [filePreviews, setFilePreviews] = useState<string[]>([]);
  const [mainImageIndex, setMainImageIndex] = useState<number>(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editingPerson, setEditingPerson] = useState<Person | null>(null);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [cropImageType, setCropImageType] = useState<'create' | 'edit' | null>(null);
  const [cropImageIndex, setCropImageIndex] = useState<number>(-1);
  const [showWebcam, setShowWebcam] = useState(false);
  const [webcamActive, setWebcamActive] = useState(false);
  const videoRef = React.useRef<HTMLVideoElement | null>(null);
  const canvasRef = React.useRef<HTMLCanvasElement | null>(null);
  const [notification, setNotification] = useState<{message: string, type: 'success' | 'error'} | null>(null);
  const notificationTimerRef = React.useRef<ReturnType<typeof setTimeout> | null>(null);
  const [isSaving, setIsSaving] = useState(false);
  const [isCapturingWebcam, setIsCapturingWebcam] = useState(false);
  
  const [searchKey, setSearchKey] = useState('');
  const [debouncedSearchKey, setDebouncedSearchKey] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(12);
  const [totalPeople, setTotalPeople] = useState(0);
  const [showDialog, setShowDialog] = useState(false);
  const [sortBy, setSortBy] = useState<'key' | 'created_at'>('created_at');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');

  const showNotification = (message: string, type: 'success' | 'error') => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
    }
    setNotification({ message, type });
    notificationTimerRef.current = setTimeout(() => setNotification(null), type === 'error' ? 20000 : 5000);
  };

  const dismissNotification = () => {
    if (notificationTimerRef.current) {
      clearTimeout(notificationTimerRef.current);
      notificationTimerRef.current = null;
    }
    setNotification(null);
  };

  const startWebcam = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ 
        video: { facingMode: 'user' } 
      });
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        setWebcamActive(true);
      }
    } catch (error) {
      console.error('Erro ao acessar webcam:', error);
      alert('Não foi possível acessar a webcam');
    }
  };

  const stopWebcam = () => {
    if (videoRef.current && videoRef.current.srcObject) {
      const stream = videoRef.current.srcObject as MediaStream;
      stream.getTracks().forEach(track => track.stop());
      videoRef.current.srcObject = null;
      setWebcamActive(false);
    }
  };

  const capturePhoto = async () => {
    if (!videoRef.current || !canvasRef.current) return;

    setIsCapturingWebcam(true);
    
    const context = canvasRef.current.getContext('2d');
    if (!context) {
      setIsCapturingWebcam(false);
      return;
    }

    canvasRef.current.width = videoRef.current.videoWidth;
    canvasRef.current.height = videoRef.current.videoHeight;
    context.drawImage(videoRef.current, 0, 0);

    canvasRef.current.toBlob(async (blob) => {
      if (!blob) {
        setIsCapturingWebcam(false);
        return;
      }

      const file = new File([blob], `webcam_${Date.now()}.jpg`, { type: 'image/jpeg' });
      const preview = URL.createObjectURL(blob);

      if (editingId) {
        setIsSaving(true);
        try {
          await peopleAPI.uploadImage(organizationId!, editingId, file, false);
          const updatedPerson = await peopleAPI.get(organizationId!, editingId);
          setEditingPerson(updatedPerson.data);
          loadPeople();
          showNotification('Foto capturada e adicionada com sucesso!', 'success');
        } catch (error: any) {
          const detail = error?.response?.data?.detail;
          const msg = typeof detail === 'object' ? detail.message : (detail || 'Erro desconhecido');
          showNotification('Erro ao adicionar foto: ' + msg, 'error');
        } finally {
          setIsSaving(false);
          setIsCapturingWebcam(false);
        }
      } else {
        setSelectedFiles([...selectedFiles, file]);
        setFilePreviews([...filePreviews, preview]);
        setIsCapturingWebcam(false);
      }

      stopWebcam();
      setShowWebcam(false);
    }, 'image/jpeg', 0.95);
  };

  useEffect(() => {
    if (organizationId) {
      loadOrganization();
    }
  }, [organizationId]);

  useEffect(() => {
    if (organizationId) {
      loadPeople();
    }
  }, [organizationId, sortBy, sortOrder, currentPage, debouncedSearchKey]);

  useEffect(() => {
    return () => {
      stopWebcam();
    };
  }, []);

  const loadOrganization = async () => {
    if (!organizationId) return;
    try {
      const response = await organizationAPI.get(organizationId);
      setOrganization(response.data);
    } catch (error) {
      console.error('Erro ao carregar organização:', error);
      showNotification('Erro ao carregar organização', 'error');
    }
  };

  const loadPeople = async (page: number = currentPage) => {
    if (!organizationId) return;
    setLoading(true);
    try {
      const skip = (page - 1) * itemsPerPage;
      const response = await peopleAPI.list(
        organizationId!,
        sortBy,
        sortOrder,
        skip,
        itemsPerPage,
        debouncedSearchKey
      );
      const totalHeader = response.headers['x-total-count'];
      const parsedTotal = Number.parseInt(totalHeader ?? '', 10);
      const nextTotalPeople = Number.isFinite(parsedTotal) ? parsedTotal : response.data.length;
      const nextTotalPages = Math.max(1, Math.ceil(nextTotalPeople / itemsPerPage));

      setTotalPeople(nextTotalPeople);

      if (page > nextTotalPages) {
        setPeople([]);
        setCurrentPage(nextTotalPages);
        return;
      }

      setPeople(response.data);
    } catch (error) {
      console.error('Erro ao carregar pessoas:', error);
    } finally {
      setLoading(false);
    }
  };

  const totalPages = Math.ceil(totalPeople / itemsPerPage);
  const paginatedPeople = people;

  useEffect(() => {
    const timeout = setTimeout(() => {
      setCurrentPage(1);
      setDebouncedSearchKey(searchKey.trim());
    }, 350);

    return () => clearTimeout(timeout);
  }, [searchKey]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!organizationId) return;
    
    setIsSaving(true);
    try {
      const personData = { ...formData, organization_id: organizationId };
      
      let personId: string;
      if (editingId) {
        await peopleAPI.update(organizationId, editingId, personData);
        personId = editingId;
      } else {
        const response = await peopleAPI.create(organizationId, personData);
        personId = response.data._id;
      }

      const photoErrors: string[] = [];
      if (!editingId && selectedFiles.length > 0) {
        for (let i = 0; i < selectedFiles.length; i++) {
          const setAsMain = i === mainImageIndex;
          try {
            await peopleAPI.uploadImage(organizationId, personId, selectedFiles[i], setAsMain);
          } catch (uploadErr: any) {
            const detail = uploadErr?.response?.data?.detail;
            const msg = typeof detail === 'object' ? detail.message : (detail || 'Erro desconhecido');
            const fileName = selectedFiles[i].name;
            photoErrors.push(`${fileName}: ${msg}`);
          }
        }
      }

      if (photoErrors.length > 0) {
        showNotification(`Pessoa criada, mas ${photoErrors.length} foto(s) rejeitada(s):\n${photoErrors.join('\n')}`, 'error');
      } else if (editingId) {
        showNotification('Pessoa atualizada com sucesso!', 'success');
      } else {
        showNotification('Pessoa criada com sucesso!', 'success');
      }
      
      closeDialog();
      loadPeople();
    } catch (error: any) {
      console.error('Erro ao salvar pessoa:', error);
      const msg = error?.response?.data?.detail || 'Erro ao salvar pessoa';
      showNotification(typeof msg === 'object' ? msg.message || 'Erro ao salvar pessoa' : msg, 'error');
    } finally {
      setIsSaving(false);
    }
  };

  const handleEdit = (person: Person) => {
    setFormData({ 
      key: person.key
    });
    setEditingId(person._id);
    setEditingPerson(person);
    setSelectedFiles([]);
    setFilePreviews([]);
    setMainImageIndex(0);
    setShowDialog(true);
  };

  const handleNewPerson = () => {
    setFormData({ key: '' });
    setEditingId(null);
    setEditingPerson(null);
    setSelectedFiles([]);
    setFilePreviews([]);
    setMainImageIndex(0);
    setShowDialog(true);
  };

  const closeDialog = () => {
    setShowDialog(false);
    setEditingId(null);
    setEditingPerson(null);
    setFormData({ key: '' });
    setSelectedFiles([]);
    setFilePreviews([]);
    setMainImageIndex(0);
    setIsSaving(false);
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Tem certeza que deseja deletar?')) {
      try {
        await peopleAPI.delete(organizationId!, id);
        showNotification('Pessoa deletada com sucesso!', 'success');
        loadPeople();
      } catch (error) {
        console.error('Erro ao deletar:', error);
        showNotification('Erro ao deletar pessoa', 'error');
      }
    }
  };

  const handleCropComplete = async (croppedBlob: Blob) => {
    try {
      if (cropImageType === 'create' && cropImageIndex >= 0) {
        const reader = new FileReader();
        reader.onloadend = () => {
          const newPreviews = [...filePreviews];
          newPreviews[cropImageIndex] = reader.result as string;
          setFilePreviews(newPreviews);

          const newFiles = [...selectedFiles];
          newFiles[cropImageIndex] = new File([croppedBlob], `cropped_${Date.now()}.jpg`, {
            type: 'image/jpeg',
          });
          setSelectedFiles(newFiles);
        };
        reader.readAsDataURL(croppedBlob);
      } else if (cropImageType === 'edit' && editingId && editingPerson) {
        try {
          await peopleAPI.replaceImage(editingId, croppedBlob, cropImageIndex);
          const updatedPerson = await peopleAPI.get(organizationId!, editingId);
          setEditingPerson(updatedPerson.data);
          loadPeople();
        } catch (error: any) {
          const detail = error?.response?.data?.detail;
          console.error('Erro ao fazer upload da imagem cropada:', error);
          alert('Erro ao salvar imagem: ' + (typeof detail === 'object' ? detail.message : (detail || 'Erro desconhecido')));
        }
      }

      setCropImageUrl(null);
      setCropImageType(null);
      setCropImageIndex(-1);
    } catch (error) {
      console.error('Erro ao processar crop:', error);
      alert('Erro ao processar crop: ' + (error instanceof Error ? error.message : 'Erro desconhecido'));
    }
  };

  if (!organizationId) {
    return (
      <div className="people-container">
        <h1>Erro</h1>
        <p>Organização não especificada.</p>
        <button className="btn-primary" onClick={() => navigate('/organizations')}>
          Voltar para Organizações
        </button>
      </div>
    );
  }

  return (
    <div className="people-container">
      {/* Botão voltar e nome da organização */}
      <div className="organization-header">
        <button className="btn-back" onClick={() => navigate('/organizations')}>
          ← Voltar para Organizações
        </button>
        {organization && (
          <div className="organization-badge">
            <span className="badge-icon">🏢</span>
            <span className="badge-name">{organization.name}</span>
          </div>
        )}
      </div>

      {/* Header com título e botão adicionar */}
      <div className="page-header">
        <h1>Pessoas</h1>
        <button 
          className="btn-add"
          onClick={handleNewPerson}
          title="Adicionar nova pessoa"
        >
          <span className="btn-icon">+</span>
          <span>Adicionar</span>
        </button>
      </div>

      {/* Dialog para criar/editar pessoa */}
      {showDialog && (
        <div className="modal-overlay" onClick={isSaving ? undefined : closeDialog} style={{ display: showWebcam ? 'none' : 'flex' }}>
          <div className="modal-content edit-dialog" onClick={(e) => e.stopPropagation()}>
            <div className="dialog-header">
              <h2>{editingId ? `✏️ Editar Pessoa: ${formData.key}` : '➕ Adicionar Nova Pessoa'}</h2>
              <button className="close-btn" onClick={closeDialog} disabled={isSaving}>✕</button>
            </div>

            {isSaving && (
              <div className="saving-overlay">
                <div className="saving-content">
                  <div className="spinner"></div>
                  <p>
                    {editingId 
                      ? 'Processando...' 
                      : selectedFiles.length > 0 
                        ? `Salvando pessoa e ${selectedFiles.length} foto${selectedFiles.length > 1 ? 's' : ''}...`
                        : 'Salvando pessoa...'
                    }
                  </p>
                </div>
              </div>
            )}

            <form id="person-form" onSubmit={handleSubmit} className="dialog-form" style={{ display: isSaving ? 'none' : 'block' }}>
              <div className="form-grid">
                <div className="form-group">
                  <label>Chave:</label>
                  <input
                    type="text"
                    value={formData.key}
                    onChange={(e) => setFormData({ ...formData, key: e.target.value })}
                    required
                  />
                </div>
              </div>

              {!editingId && (
                <>
                  <div className="form-group form-group-photos">
                    <label>Fotos (múltiplas):</label>
                    <div 
                      className="file-upload-wrapper"
                      onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('drag-over');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('drag-over');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('drag-over');
                    
                    if (e.dataTransfer.files) {
                      const newFiles = Array.from(e.dataTransfer.files).filter(
                        file => file.type.startsWith('image/')
                      );
                      
                      if (newFiles.length > 0) {
                        const allFiles = [...selectedFiles, ...newFiles];
                        setSelectedFiles(allFiles);
                        
                        const newPreviews: string[] = [];
                        newFiles.forEach((file) => {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            newPreviews.push(reader.result as string);
                            if (newPreviews.length === newFiles.length) {
                              setFilePreviews([...filePreviews, ...newPreviews]);
                            }
                          };
                          reader.readAsDataURL(file);
                        });
                      }
                    }
                  }}
                >
                  <label className="file-upload-label" htmlFor="file-input-create">
                    <div className="file-upload-icon">📷</div>
                    <div className="file-upload-text">
                      <strong>Clique para adicionar fotos</strong>
                      <p>ou arraste arquivos aqui</p>
                    </div>
                  </label>
                  <input
                    id="file-input-create"
                    type="file"
                    accept="image/*"
                    multiple
                    onChange={(e) => {
                      if (e.target.files) {
                        const newFiles = Array.from(e.target.files);
                        const allFiles = [...selectedFiles, ...newFiles];
                        setSelectedFiles(allFiles);
                        
                        const newPreviews: string[] = [];
                        newFiles.forEach((file) => {
                          const reader = new FileReader();
                          reader.onloadend = () => {
                            newPreviews.push(reader.result as string);
                            if (newPreviews.length === newFiles.length) {
                              setFilePreviews([...filePreviews, ...newPreviews]);
                            }
                          };
                          reader.readAsDataURL(file);
                        });
                        
                        e.currentTarget.value = '';
                      }
                    }}
                  />
                    </div>
                    <button
                      type="button"
                      className="btn-secondary webcam-btn"
                      onClick={() => setShowWebcam(true)}
                    >
                      📷 Capturar com Webcam
                    </button>
                  </div>

                  {filePreviews.length > 0 && (
                    <div className="images-management-preview">
                  <h3>📸 Fotos Selecionadas ({filePreviews.length})</h3>
                  
                  {mainImageIndex < filePreviews.length && (
                    <div className="main-image-section">
                      <p><strong>Foto Principal</strong></p>
                      <img 
                        src={filePreviews[mainImageIndex]} 
                        alt="Foto principal"
                        className="main-image-edit" 
                      />
                    </div>
                  )}

                  {filePreviews.length > 1 && (
                    <div className="images-gallery-edit">
                      <p><strong>Todas as Fotos ({filePreviews.length})</strong></p>
                      <div className="gallery-grid-edit">
                        {filePreviews.map((preview: string, idx: number) => (
                          <div key={idx} className="gallery-item-edit">
                            <div className={`image-wrapper-edit ${idx === mainImageIndex ? 'main' : ''}`}>
                              <img src={preview} alt={`Foto ${idx + 1}`} />
                              {idx === mainImageIndex && <span className="main-badge-edit">Principal</span>}
                            </div>
                            <div className="image-actions-edit">
                              <button 
                                type="button"
                                className={`icon-btn ${idx === mainImageIndex ? 'active' : ''}`}
                                onClick={(e) => {
                                  e.preventDefault();
                                  if (idx !== mainImageIndex) {
                                    setMainImageIndex(idx);
                                  }
                                }}
                                title={idx === mainImageIndex ? "Foto principal" : "Definir como principal"}
                                disabled={idx === mainImageIndex}
                              >
                                ⭐
                              </button>
                              <button
                                type="button"
                                className="icon-btn"
                                onClick={(e) => {
                                  e.preventDefault();
                                  setCropImageUrl(preview);
                                  setCropImageType('create');
                                  setCropImageIndex(idx);
                                }}
                                title="Editar imagem com crop"
                              >
                                ✏️
                              </button>
                              <button 
                                type="button"
                                className="icon-btn danger"
                                onClick={(e) => {
                                  e.preventDefault();
                                  const newPreviews = filePreviews.filter((_, i) => i !== idx);
                                  const newFiles = selectedFiles.filter((_, i) => i !== idx);
                                  setFilePreviews(newPreviews);
                                  setSelectedFiles(newFiles);
                                  
                                  if (idx === mainImageIndex) {
                                    setMainImageIndex(0);
                                  } else if (mainImageIndex > idx) {
                                    setMainImageIndex(mainImageIndex - 1);
                                  }
                                }}
                                title="Remover imagem"
                              >
                                🗑️
                              </button>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                    </div>
                  )}
                </>
              )}

              {/* Gerenciamento de imagens no modo de edição */}
              {editingId && editingPerson && (
                <div className="images-management">
                  <h3>📸 Gerenciar Fotos</h3>
                  
                  {editingPerson.image_url && (
                    <div className="main-image-section">
                  <p><strong>Foto Principal</strong></p>
                  <img 
                    src={getImageUrl(editingPerson.image_url)} 
                    alt={editingPerson.key}
                    className="main-image-edit" 
                  />
                </div>
              )}
              
              {editingPerson.images && editingPerson.images.length > 0 && (
                <div className="images-gallery-edit">
                  <p><strong>Todas as Fotos ({editingPerson.images.length})</strong></p>
                  <div className="gallery-grid-edit">
                    {editingPerson.images.map((img: any, idx: number) => {
                      const filename = (img.url || '').replace(/^\/images\//, '');
                      const isMain = img.url === editingPerson.image_url;
                      return (
                        <div key={idx} className="gallery-item-edit">
                          <div className={`image-wrapper-edit ${isMain ? 'main' : ''}`}>
                            <img src={getImageUrl(img.url)} alt={`Foto ${idx + 1}`} />
                            {isMain && <span className="main-badge-edit">Principal</span>}
                            {Array.isArray(img.warnings) && img.warnings.length > 0 && (
                              <span
                                className="warning-badge-edit"
                                title={img.warnings.join('\n')}
                              >
                                !
                              </span>
                            )}
                          </div>
                          <div className="image-actions-edit">
                            <button 
                              type="button"
                              className={`icon-btn ${isMain ? 'active' : ''}`}
                              onClick={async (e) => {
                                e.preventDefault();
                                if (!isMain) {
                                  setIsSaving(true);
                                  try {
                                    await peopleAPI.setMainImage(organizationId!, editingId!, img.url);
                                    const response = await peopleAPI.get(organizationId!, editingId!);
                                    setEditingPerson(response.data);
                                    loadPeople();
                                    showNotification('Imagem principal definida!', 'success');
                                  } catch (error) {
                                    console.error('Erro ao definir imagem principal:', error);
                                    showNotification('Erro ao definir imagem principal', 'error');
                                  } finally {
                                    setIsSaving(false);
                                  }
                                }
                              }}
                              title={isMain ? "Foto principal" : "Definir como principal"}
                              disabled={isMain}
                            >
                              ⭐
                            </button>
                            <button
                              type="button"
                              className="icon-btn"
                              onClick={(e) => {
                                e.preventDefault();
                                setCropImageUrl(getImageUrl(img.url));
                                setCropImageType('edit');
                                setCropImageIndex(idx);
                              }}
                              title="Editar imagem com crop"
                            >
                              ✏️
                            </button>
                            <button 
                              type="button"
                              className="icon-btn danger"
                              onClick={async (e) => {
                                e.preventDefault();
                                if (window.confirm('Tem certeza que deseja deletar esta imagem?')) {
                                  setIsSaving(true);
                                  try {
                                    await peopleAPI.deleteImage(organizationId!, editingId!, filename);
                                    const updatedPerson = await peopleAPI.get(organizationId!, editingId!);
                                    setEditingPerson(updatedPerson.data);
                                    loadPeople();
                                    showNotification('Imagem deletada com sucesso!', 'success');
                                  } catch (error: any) {
                                    console.error('Erro ao deletar imagem:', error);
                                    const detail = error?.response?.data?.detail;
                                    showNotification('Erro ao deletar imagem: ' + (typeof detail === 'object' ? detail.message : (detail || 'Erro desconhecido')), 'error');
                                  } finally {
                                    setIsSaving(false);
                                  }
                                }
                              }}
                              title="Deletar imagem"
                            >
                              🗑️
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                </div>
              )}

                  <div className="upload-more-images-edit">
                    <label>Adicionar Mais Fotos:</label>
                <div 
                  className="file-upload-wrapper"
                  onDragOver={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.add('drag-over');
                  }}
                  onDragLeave={(e) => {
                    e.currentTarget.classList.remove('drag-over');
                  }}
                  onDrop={(e) => {
                    e.preventDefault();
                    e.currentTarget.classList.remove('drag-over');
                    
                    if (e.dataTransfer.files) {
                      const newFiles = Array.from(e.dataTransfer.files).filter(
                        file => file.type.startsWith('image/')
                      );
                      
                      if (newFiles.length > 0 && editingId) {
                        const uploadFiles = async () => {
                          setIsSaving(true);
                          try {
                            for (let i = 0; i < newFiles.length; i++) {
                              try {
                                await peopleAPI.uploadImage(organizationId!, editingId, newFiles[i], false);
                                const response = await peopleAPI.get(organizationId!, editingId);
                                setEditingPerson(response.data);
                                loadPeople();
                              } catch (error: any) {
                                const detail = error?.response?.data?.detail;
                                const msg = typeof detail === 'object' ? detail.message : (detail || 'Erro desconhecido');
                                showNotification(`Foto ${newFiles[i].name}: ${msg}`, 'error');
                              }
                            }
                          } finally {
                            setIsSaving(false);
                          }
                        };
                        uploadFiles();
                      }
                    }
                  }}
                >
                  <label className="file-upload-label-edit" htmlFor="file-input-edit">
                    <div className="file-upload-icon">📷</div>
                    <div className="file-upload-text">
                      <strong>Clique para adicionar fotos</strong>
                      <p>ou arraste arquivos aqui</p>
                    </div>
                  </label>
                  <input 
                    id="file-input-edit"
                    type="file" 
                    accept="image/*" 
                    multiple
                    onChange={async (e) => {
                      const files = e.currentTarget.files;
                      const inputEl = e.currentTarget;
                      if (files && editingId) {
                        setIsSaving(true);
                        try {
                          for (let i = 0; i < files.length; i++) {
                            try {
                              await peopleAPI.uploadImage(organizationId!, editingId, files[i], false);
                              const response = await peopleAPI.get(organizationId!, editingId);
                              setEditingPerson(response.data);
                              loadPeople();
                            } catch (error: any) {
                              const detail = error?.response?.data?.detail;
                              const msg = typeof detail === 'object' ? detail.message : (detail || 'Erro desconhecido');
                              showNotification(`Foto ${files[i].name}: ${msg}`, 'error');
                            }
                          }
                        } finally {
                          setIsSaving(false);
                        }
                      }
                      inputEl.value = '';
                    }}
                    className="file-input-edit"
                  />
                </div>
                
                <button
                  type="button"
                  className="btn-secondary webcam-btn-edit"
                  onClick={() => setShowWebcam(true)}
                >
                  📷 Capturar com Webcam
                    </button>
                  </div>
                </div>
              )}
            </form>
            
            <div className="dialog-actions" style={{ display: isSaving ? 'none' : 'flex' }}>
              <button type="submit" form="person-form" className="btn-primary" disabled={isSaving}>
                💾 {editingId ? 'Salvar' : 'Criar'}
              </button>
              <button 
                type="button" 
                className="btn-secondary"
                onClick={closeDialog}
                disabled={isSaving}
              >
                Cancelar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Barra de busca e ordenação */}
      <div className="search-bar">
        <div className="search-input-wrapper">
          <input
            type="text"
            placeholder="🔍 Buscar por chave ou ID..."
            value={searchKey}
            onChange={(e) => setSearchKey(e.target.value)}
            className="search-input"
          />
          {searchKey && (
            <button 
              className="clear-search"
              onClick={() => setSearchKey('')}
              title="Limpar busca"
            >
              ✕
            </button>
          )}
        </div>
        
        <select 
          className="sort-select"
          value={`${sortBy}-${sortOrder}`}
          onChange={(e) => {
            const [field, order] = e.target.value.split('-') as ['key' | 'created_at', 'asc' | 'desc'];
            setSortBy(field);
            setSortOrder(order);
            setCurrentPage(1);
          }}
        >
          <option value="key-asc">Chave (A-Z)</option>
          <option value="key-desc">Chave (Z-A)</option>
          <option value="created_at-desc">Mais recentes</option>
          <option value="created_at-asc">Mais antigas</option>
        </select>
        
        <span className="search-count">
          {totalPeople} pessoa{totalPeople !== 1 ? 's' : ''}
        </span>
      </div>

      {/* Lista de pessoas com paginação */}
      {loading ? (
        <p>Carregando...</p>
      ) : (
        <>
          <div className="people-list">
            {paginatedPeople.length > 0 ? (
              paginatedPeople.map((person) => (
                <div key={person._id} className="person-card">
                  {person.image_url ? (
                    <img src={getImageUrl(person.image_url)} alt={person.key} className="person-image" />
                  ) : (
                    <div className="person-image-placeholder">
                      <svg viewBox="0 0 24 24" fill="currentColor" width="64" height="64">
                        <path d="M12 12c2.76 0 5-2.24 5-5s-2.24-5-5-5-5 2.24-5 5 2.24 5 5 5zm0 2c-3.33 0-10 1.67-10 5v2h20v-2c0-3.33-6.67-5-10-5z"/>
                      </svg>
                    </div>
                  )}
                  <h3>{person.key}</h3>
                  <span className="person-id-label">{person._id}</span>
                  <p className="date">Criado em: {formatDate(person.created_at)}</p>
                  
                  <div className="actions">
                    <button 
                      className="btn-edit"
                      onClick={() => handleEdit(person)}
                    >
                      Editar
                    </button>
                    <button 
                      className="btn-delete"
                      onClick={() => handleDelete(person._id)}
                    >
                      Deletar
                    </button>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">
                <p>Nenhuma pessoa encontrada{searchKey && ` com a chave "${searchKey}"`}</p>
              </div>
            )}
          </div>

          {/* Paginação */}
          <Pagination
            currentPage={currentPage}
            totalPages={totalPages}
            onPageChange={setCurrentPage}
          />
        </>
      )}

      {cropImageUrl && (
        <ImageCropModal
          image={cropImageUrl}
          organizationId={organizationId!}
          onCropComplete={handleCropComplete}
          onCancel={() => {
            setCropImageUrl(null);
            setCropImageType(null);
            setCropImageIndex(-1);
          }}
          onValidationError={(error) => {
            alert(`Erro ao validar face:\n\n${error}\n\nTente fazer um crop que inclua melhor o rosto.`);
          }}
        />
      )}

      {/* Modal de Webcam */}
      {showWebcam && (
        <div className="modal-overlay" onClick={() => {
          stopWebcam();
          setShowWebcam(false);
        }}>
          <div className="modal-content webcam-modal" onClick={(e) => e.stopPropagation()}>
            <h2>📷 Capturar Foto com Webcam</h2>
            
            <div className="webcam-container">
              <video
                ref={videoRef}
                className="webcam-video"
                style={{ display: webcamActive ? 'block' : 'none' }}
                autoPlay
                playsInline
                muted
              />
              
              {!webcamActive && (
                <div className="webcam-placeholder">
                  <p>Clique em "Iniciar Webcam" para começar</p>
                </div>
              )}
            </div>

            <div className="webcam-controls">
              {!webcamActive ? (
                <button
                  type="button"
                  className="btn-primary"
                  onClick={startWebcam}
                  disabled={isCapturingWebcam}
                >
                  ▶️ Iniciar Webcam
                </button>
              ) : (
                <>
                  <button
                    type="button"
                    className="btn-success"
                    onClick={capturePhoto}
                    disabled={isCapturingWebcam}
                  >
                    {isCapturingWebcam ? (
                      <>
                        <span className="btn-spinner"></span>
                        Processando...
                      </>
                    ) : (
                      <>📸 Capturar Foto</>
                    )}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    onClick={stopWebcam}
                    disabled={isCapturingWebcam}
                  >
                    ⏹️ Parar Webcam
                  </button>
                </>
              )}
              <button
                type="button"
                className="btn-secondary"
                onClick={() => {
                  stopWebcam();
                  setShowWebcam(false);
                }}
                disabled={isCapturingWebcam}
              >
                ❌ Fechar
              </button>
            </div>

            <canvas ref={canvasRef} style={{ display: 'none' }} />
          </div>
        </div>
      )}

      {/* Notificação */}
      {notification && (
        <div className={`notification ${notification.type}`}>
          <span>{notification.type === 'success' ? '✅' : '❌'}</span>
          <span className="notification-message">{notification.message}</span>
          <button
            className="notification-dismiss"
            onClick={dismissNotification}
            title={notification.type === 'error' ? 'Fechar' : 'OK'}
          >
            {notification.type === 'error' ? '✕' : 'OK'}
          </button>
        </div>
      )}
    </div>
  );
}

export default People;
